import { gql } from "@apollo/client";
import { compose, withHooks, withStores, withTranslation } from "enhancers";
import { lazy, Suspense } from "react";
import { useIdleTimer } from "react-idle-timer";
import paths, { getTenantIdFromUrl } from "routes/paths";

import appStore from "stores/appStore";

import { Modal, Redirect, Route, Switch } from "components";
import Loader from "components/Loader";
import { BlankLayout, MenuLayout } from "layouts";

import { getLoggingAd, getRefreshToken, getToken } from "api";
import LanguagesDropdown from "components/common/LanguagesDropdown";
import { HandleAdRedirectPage } from "pages/main/authentication/HandleAdRedirectPage";
import { LoginPage } from "pages/main/authentication/LoginPage";

import { useMsal } from "@azure/msal-react";
import { PERMISSIONS } from "constants/enums/permissions";
import { isEmpty } from "lodash";
import { useCallback } from "react";
import { getTenantPath, homePath } from "utils/helper";

const AdminEditPage = lazy(() => import("pages/main/admins/edit"));
const AdminIndexPage = lazy(() => import("pages/main/admins/index"));
const AdminNewPage = lazy(() => import("pages/main/admins/new"));

const RoleEditPage = lazy(() => import("pages/main/roles/edit"));
const RoleIndexPage = lazy(() => import("pages/main/roles/index"));
const RoleNewPage = lazy(() => import("pages/main/roles/new"));

const SettingsIndexPage = lazy(() => import("pages/main/settings"));
const InsuranceFormPage = lazy(() =>
  import("pages/main/insuranceManagement/InsuranceForm").then((m) => ({
    default: m.InsuranceFormPage,
  }))
);
const InsuranceDetailPage = lazy(() =>
  import("pages/main/insuranceManagement/detail").then((m) => ({
    default: m.InsuranceDetailPage,
  }))
);
const dynamicConfig = lazy(() => import("pages/main/dynamicConfig"));
const EnrollmentPage = lazy(() =>
  import("pages/main/enrollment").then((m) => ({ default: m.EnrollmentPage }))
);
const EnrollmentDetailPage = lazy(() =>
  import("pages/main/enrollment/detail").then((m) => ({
    default: m.EnrollmentDetailPage,
  }))
);
const EnrollmentInsurancePage = lazy(() =>
  import("pages/main/enrollment/enrollmentInsurance").then((m) => ({
    default: m.EnrollmentInsurancePage,
  }))
);
const InsuranceLandingPage = lazy(() =>
  import("pages/main/insuranceManagement").then((m) => ({
    default: m.InsuranceLandingPage,
  }))
);
const ViewInsurancePage = lazy(
  () => import("pages/main/insuranceManagement/insurancePackages/ViewInsurance")
);
const InsurancePackagesDuplicatePage = lazy(
  () => import("pages/main/insuranceManagement/insurancePackages/duplicate")
);
const InsurancePackagesEditPage = lazy(
  () => import("pages/main/insuranceManagement/insurancePackages/edit")
);
const InsurancePackagesNewPage = lazy(
  () => import("pages/main/insuranceManagement/insurancePackages/new")
);
const InsuranceCompanyPage = lazy(() =>
  import("pages/main/insuranceCompany").then((m) => ({
    default: m.InsuranceCompanyPage,
  }))
);
const InsuranceCompanyEditPage = lazy(() =>
  import("pages/main/insuranceCompany/edit").then((m) => ({
    default: m.InsuranceCompanyEditPage,
  }))
);
const MarketplaceReportPage = lazy(
  () => import("pages/main/marketplaceReport")
);
const InsurancePlanPage = lazy(() =>
  import("pages/main/masterInsurancePlans").then((m) => ({
    default: m.InsurancePlanPage,
  }))
);
const MasterInsurancePlanDetailPage = lazy(() =>
  import("pages/main/masterInsurancePlans/detail").then((m) => ({
    default: m.MasterInsurancePlanDetailPage,
  }))
);
const OccupationIndexPage = lazy(() => import("pages/main/occupation"));
const OccupationEditPage = lazy(() =>
  import("pages/main/occupation/edit").then((m) => ({
    default: m.OccupationEditPage,
  }))
);
const OccupationNewPage = lazy(() =>
  import("pages/main/occupation/new").then((m) => ({
    default: m.OccupationNewPage,
  }))
);
const PaymentTransactionIndexPage = lazy(
  () => import("pages/main/paymentTransaction")
);
const PaymentTransactionDetailPage = lazy(
  () => import("pages/main/paymentTransaction/detail")
);
const ReportPage = lazy(() =>
  import("pages/main/report").then((m) => ({ default: m.ReportPage }))
);
const WorkspaceEditPage = lazy(() =>
  import("pages/main/workspace/edit").then((m) => ({
    default: m.WorkspaceEditPage,
  }))
);
const WorkspaceIndexPage = lazy(() => import("pages/main/workspace/index"));
const WorkspaceNewPage = lazy(() =>
  import("pages/main/workspace/new").then((m) => ({
    default: m.WorkspaceNewPage,
  }))
);

const InsuranceTransactionIndexPage = lazy(
  () => import("pages/main/insuranceTransaction")
);
const InsuranceTransactionDetailPage = lazy(
  () => import("pages/main/insuranceTransaction/detail")
);
const OverdueTransactionIndexPage = lazy(
  () => import("pages/main/overdueTransaction")
);
const LeadPage = lazy(() =>
  import("pages/main/lead").then((m) => ({ default: m.LeadPage }))
);
const UserPage = lazy(() =>
  import("pages/main/user").then((m) => ({ default: m.UserPage }))
);
const UserDetailPage = lazy(() =>
  import("pages/main/user/detail").then((m) => ({
    default: m.UserDetailPage,
  }))
);

const NotificationManagementPage = lazy(
  () => import("pages/main/notificationManagement")
);
const NotificationNewPage = lazy(() =>
  import("pages/main/notificationManagement/new").then((m) => ({
    default: m.NotificationNewPage,
  }))
);
const NotificationEditPage = lazy(() =>
  import("pages/main/notificationManagement/edit").then((m) => ({
    default: m.NotificationEditPage,
  }))
);
const NotificationHistoryPage = lazy(
  () => import("pages/main/notificationManagement/history")
);
const FrontStoreManagementPage = lazy(
  () => import("pages/main/frontStoreManagement")
);
const FrontStoreTabsContentPage = lazy(
  () => import("pages/main/frontStoreManagement/FrontStoreTabsContent")
);
const AddFatcaQuestionPage = lazy(
  () => import("pages/main/frontStoreManagement/fatca-questions/add")
);
const EditFatcaQuestionPage = lazy(
  () => import("pages/main/frontStoreManagement/fatca-questions/edit")
);
const NameChangeRequestPage = lazy(
  () => import("pages/main/nameChangeRequest")
);
const InsuranceValidateDetail = lazy(
  () => import("pages/main/insuranceManagement/InsuranceValidateDetail")
);
const BrokerPage = lazy(() =>
  import("pages/main/broker").then((m) => ({ default: m.BrokerPage }))
);
const BrokerNewPage = lazy(() =>
  import("pages/main/broker/new").then((m) => ({ default: m.BrokerNewPage }))
);
const BrokerEditPage = lazy(() =>
  import("pages/main/broker/edit").then((m) => ({
    default: m.BrokerEditPage,
  }))
);
const NewHealthQuestionPage = lazy(() =>
  import("pages/main/insuranceCompany/healthQuestion/new").then((m) => ({
    default: m.NewHealthQuestionPage,
  }))
);
const EditHealthQuestionPage = lazy(() =>
  import("pages/main/insuranceCompany/healthQuestion/edit").then((m) => ({
    default: m.EditHealthQuestionPage,
  }))
);
const InstallmentDetail = lazy(
  () => import("pages/main/insuranceTransaction/InstallmentDetail")
);
const OrderPage = lazy(() =>
  import("pages/main/order").then((m) => ({ default: m.OrderPage }))
);
const OrderDetailPage = lazy(() =>
  import("pages/main/order/detail").then((m) => ({
    default: m.OrderDetailPage,
  }))
);

// prettier-ignore
const GuestPages = () => (
  <BlankLayout>
    <LanguagesDropdown />
    <Switch>
      <Route path={paths.signInPath()} exact component={LoginPage} />
      {/* <Route path={paths.setupPasswordPath()}  exact component={SetupPasswordPage} /> */}
      {/* <Route path={paths.resetPasswordPath()}  exact component={ResetPasswordPage} /> */}

      <Redirect to={paths.signInPath()} />
    </Switch>
  </BlankLayout>
);

const HandleAdRedirectPages = () => (
  <BlankLayout>
    <Switch>
      <Route path={paths.signInPath()} exact component={HandleAdRedirectPage} />
      <Redirect to={paths.signInPath()} />
    </Switch>
  </BlankLayout>
);

// Master Tenant Routes (workspace, admins, roles only)
const MasterPages = () => (
  <Switch>
    <Route
      path="/:tenantId/workspace"
      exact
      layout={MenuLayout}
      component={WorkspaceIndexPage}
      permittedRoles={[PERMISSIONS.WORKSPACE_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/workspace/new"
      exact
      layout={MenuLayout}
      component={WorkspaceNewPage}
      permittedRoles={[PERMISSIONS.WORKSPACE_MANAGEMENT_EDIT]}
    />
    <Route
      path="/:tenantId/workspace/:id/edit"
      exact
      layout={MenuLayout}
      component={WorkspaceEditPage}
      permittedRoles={[PERMISSIONS.WORKSPACE_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/admins/new"
      exact
      layout={MenuLayout}
      component={AdminNewPage}
      permittedRoles={["ADMIN_MANAGEMENT_READ"]}
    />
    <Route
      path="/:tenantId/admins"
      exact
      layout={MenuLayout}
      component={AdminIndexPage}
      permittedRoles={["ADMIN_MANAGEMENT_READ"]}
    />
    <Route
      path="/:tenantId/admins/:id/edit"
      exact
      layout={MenuLayout}
      component={AdminEditPage}
      permittedRoles={["ADMIN_MANAGEMENT_READ"]}
    />
    <Route
      path="/:tenantId/roles"
      exact
      layout={MenuLayout}
      component={RoleIndexPage}
      permittedRoles={["PERMISSION_MANAGEMENT_READ"]}
    />
    <Route
      path="/:tenantId/roles/new"
      exact
      layout={MenuLayout}
      component={RoleNewPage}
      permittedRoles={["PERMISSION_MANAGEMENT_READ"]}
    />
    <Route
      path="/:tenantId/roles/:id/edit"
      exact
      layout={MenuLayout}
      component={RoleEditPage}
      permittedRoles={["PERMISSION_MANAGEMENT_READ"]}
    />

    <Redirect withPermissions to="/:tenantId/workspace" />
  </Switch>
);

const TenantedPages = () => (
  <Switch>
    <Route
      path="/:tenantId/admins/new"
      exact
      layout={MenuLayout}
      component={AdminNewPage}
      permittedRoles={["ADMIN_MANAGEMENT_READ"]}
    />
    <Route
      path="/:tenantId/admins"
      exact
      layout={MenuLayout}
      component={AdminIndexPage}
      permittedRoles={["ADMIN_MANAGEMENT_READ"]}
    />
    <Route
      path="/:tenantId/admins/:id/edit"
      exact
      layout={MenuLayout}
      component={AdminEditPage}
      permittedRoles={["ADMIN_MANAGEMENT_READ"]}
    />
    <Route
      path="/:tenantId/roles"
      exact
      layout={MenuLayout}
      component={RoleIndexPage}
      permittedRoles={["PERMISSION_MANAGEMENT_READ"]}
    />
    <Route
      path="/:tenantId/roles/new"
      exact
      layout={MenuLayout}
      component={RoleNewPage}
      permittedRoles={["PERMISSION_MANAGEMENT_READ"]}
    />
    <Route
      path="/:tenantId/roles/:id/edit"
      exact
      layout={MenuLayout}
      component={RoleEditPage}
      permittedRoles={["PERMISSION_MANAGEMENT_READ"]}
    />
    {/* <Route
      path="/:tenantId/settings"
      exact
      layout={MenuLayout}
      component={SettingsIndexPage}
      permittedRoles={[PERMISSIONS.SETTING_MANAGEMENT_READ]}
    /> */}
    <Route
      path="/:tenantId/insurance/:insuranceId/packages/new"
      exact
      layout={MenuLayout}
      component={InsurancePackagesNewPage}
      permittedRoles={[PERMISSIONS.INSURANCE_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/insurance/:insuranceId/packages/:id/edit"
      exact
      layout={MenuLayout}
      component={InsurancePackagesEditPage}
      permittedRoles={[PERMISSIONS.INSURANCE_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/insurance/:insuranceId/packages/duplicate/:duplicateId"
      exact
      layout={MenuLayout}
      component={InsurancePackagesDuplicatePage}
      permittedRoles={[PERMISSIONS.INSURANCE_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/insurance/:insuranceId/packages/insurance/:id"
      exact
      layout={MenuLayout}
      component={ViewInsurancePage}
      permittedRoles={[PERMISSIONS.INSURANCE_MANAGEMENT_READ]}
    />
    {/* <Route
      path="/:tenantId/insurance/:id/insurance_creating/new"
      exact
      layout={MenuLayout}
      component={InsuranceFormPage}
      permittedRoles={[PERMISSIONS.INSURANCE_MANAGEMENT_READ]}
    />
    */}
    <Route
      path="/:tenantId/insurance/:id/insurance_creating/edit/:insuranceId"
      exact
      layout={MenuLayout}
      component={InsuranceFormPage}
      permittedRoles={[PERMISSIONS.INSURANCE_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/insurance/:id/insurance_creating/duplicate/:duplicateId"
      exact
      layout={MenuLayout}
      component={InsuranceFormPage}
      permittedRoles={[PERMISSIONS.INSURANCE_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/insurance/:insuranceId/validating/:id"
      exact
      layout={MenuLayout}
      component={InsuranceValidateDetail}
      permittedRoles={[PERMISSIONS.INSURANCE_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/insurance/:id"
      layout={MenuLayout}
      component={InsuranceDetailPage}
      permittedRoles={[PERMISSIONS.INSURANCE_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/insurance"
      exact
      layout={MenuLayout}
      component={InsuranceLandingPage}
      permittedRoles={[PERMISSIONS.INSURANCE_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/enrollment/:id/insurance_plans/:insuranceId"
      exact
      layout={MenuLayout}
      component={EnrollmentInsurancePage}
      permittedRoles={[PERMISSIONS.ENROLLMENT_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/enrollment"
      exact
      layout={MenuLayout}
      component={EnrollmentPage}
      permittedRoles={[PERMISSIONS.ENROLLMENT_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/enrollment/:id"
      layout={MenuLayout}
      component={EnrollmentDetailPage}
      permittedRoles={[PERMISSIONS.ENROLLMENT_MANAGEMENT_READ]}
    />
    {/* <Route
      path="/:tenantId/report"
      exact
      layout={MenuLayout}
      component={ReportPage}
      permittedRoles={[PERMISSIONS.REPORT_MANAGEMENT_READ]}
    /> */}
    {/* <Route
      path="/:tenantId/marketplace_report"
      exact
      layout={MenuLayout}
      component={MarketplaceReportPage}
      permittedRoles={[PERMISSIONS.REPORT_MANAGEMENT_READ]}
    /> */}
    <Route
      path="/:tenantId/insurance_company"
      exact
      layout={MenuLayout}
      component={InsuranceCompanyPage}
      permittedRoles={[PERMISSIONS.INSURANCE_COMPANY_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/insurance_company/:id/edit"
      exact
      layout={MenuLayout}
      component={InsuranceCompanyEditPage}
      permittedRoles={[PERMISSIONS.INSURANCE_COMPANY_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/insurance_company/:id/question/new"
      exact
      layout={MenuLayout}
      component={NewHealthQuestionPage}
      permittedRoles={[PERMISSIONS.INSURANCE_COMPANY_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/insurance_company/:id/question/:questionId/edit"
      exact
      layout={MenuLayout}
      component={EditHealthQuestionPage}
      permittedRoles={[PERMISSIONS.INSURANCE_COMPANY_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/dynamic_config"
      exact
      layout={MenuLayout}
      component={dynamicConfig}
      permittedRoles={[PERMISSIONS.FRONT_STORE_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/notification_management"
      exact
      layout={MenuLayout}
      component={NotificationManagementPage}
      permittedRoles={[PERMISSIONS.NOTIFICATION_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/notification_management/:id/edit"
      exact
      layout={MenuLayout}
      component={NotificationEditPage}
      permittedRoles={[PERMISSIONS.NOTIFICATION_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/notification_management/new"
      exact
      layout={MenuLayout}
      component={NotificationNewPage}
      permittedRoles={[PERMISSIONS.NOTIFICATION_MANAGEMENT_EDIT]}
    />
    <Route
      path="/:tenantId/notification_management/history"
      exact
      layout={MenuLayout}
      component={NotificationHistoryPage}
      permittedRoles={[PERMISSIONS.NOTIFICATION_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/occupation"
      exact
      layout={MenuLayout}
      component={OccupationIndexPage}
      permittedRoles={[PERMISSIONS.OCCUPATION_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/occupation/new"
      exact
      layout={MenuLayout}
      component={OccupationNewPage}
      permittedRoles={[PERMISSIONS.OCCUPATION_MANAGEMENT_EDIT]}
    />
    <Route
      path="/:tenantId/occupation/:id/edit"
      exact
      layout={MenuLayout}
      component={OccupationEditPage}
      permittedRoles={[PERMISSIONS.OCCUPATION_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/master_insurance_plan"
      exact
      layout={MenuLayout}
      component={InsurancePlanPage}
      permittedRoles={[PERMISSIONS.MASTER_INSURANCE_PLAN_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/master_insurance_plan/:id"
      exact
      layout={MenuLayout}
      component={MasterInsurancePlanDetailPage}
      permittedRoles={[PERMISSIONS.MASTER_INSURANCE_PLAN_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/payment_transaction"
      exact
      layout={MenuLayout}
      component={PaymentTransactionIndexPage}
      permittedRoles={[PERMISSIONS.PAYMENT_TRANSACTION_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/payment_transaction/:id"
      exact
      layout={MenuLayout}
      component={PaymentTransactionDetailPage}
      permittedRoles={[PERMISSIONS.PAYMENT_TRANSACTION_MANAGEMENT_READ]}
    />
    {/* <Route
      path="/:tenantId/insurance_plan_enterprise/:id"
      exact
      layout={MenuLayout}
      component={EnterpriseForm}
    /> */}

    <Route
      path="/:tenantId/insurance_transaction"
      exact
      layout={MenuLayout}
      component={InsuranceTransactionIndexPage}
      permittedRoles={[PERMISSIONS.INSURANCE_TRANSACTION_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/insurance_transaction/:id"
      exact
      layout={MenuLayout}
      component={InsuranceTransactionDetailPage}
      permittedRoles={[PERMISSIONS.INSURANCE_TRANSACTION_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/insurance_transaction/:id/installment/:installmentId"
      exact
      layout={MenuLayout}
      component={InstallmentDetail}
      permittedRoles={[PERMISSIONS.INSURANCE_TRANSACTION_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/overdue_transaction"
      exact
      layout={MenuLayout}
      component={OverdueTransactionIndexPage}
      permittedRoles={[]}
    />
    <Route
      path={`/:tenantId/lead`}
      layout={MenuLayout}
      component={LeadPage}
      permittedRoles={[PERMISSIONS.LEAD_MANAGEMENT_READ]}
    />
    <Route
      path={`/:tenantId/user`}
      exact
      layout={MenuLayout}
      component={UserPage}
      permittedRoles={[PERMISSIONS.USER_MANAGEMENT_READ]}
    />
    <Route
      path={`/:tenantId/user/:id`}
      exact
      layout={MenuLayout}
      component={UserDetailPage}
      permittedRoles={[PERMISSIONS.USER_MANAGEMENT_READ]}
    />
    {/* <Route
      path={`/:tenantId/rider_config`}
      exact
      layout={MenuLayout}
      component={RiderConfigPage}
      permittedRoles={[]}
    /> */}
    <Route
      path={`/:tenantId/front_store_management`}
      exact
      layout={MenuLayout}
      component={FrontStoreManagementPage}
      permittedRoles={[]}
    />
    <Route
      path={`/:tenantId/front_store_management/:id/fatca-question/add`}
      exact
      layout={MenuLayout}
      component={AddFatcaQuestionPage}
      permittedRoles={[]}
    />
    <Route
      path={`/:tenantId/front_store_management/:id/fatca-question/:questionId/edit`}
      exact
      layout={MenuLayout}
      component={EditFatcaQuestionPage}
      permittedRoles={[]}
    />
    <Route
      path={`/:tenantId/front_store_management/:id`}
      exact
      layout={MenuLayout}
      component={FrontStoreTabsContentPage}
      permittedRoles={[]}
    />
    <Route
      path="/:tenantId/broker/new"
      exact
      layout={MenuLayout}
      component={BrokerNewPage}
      permittedRoles={[PERMISSIONS.BROKER_MANAGEMENT_EDIT]}
    />
    <Route
      path="/:tenantId/broker/:id/edit"
      exact
      layout={MenuLayout}
      component={BrokerEditPage}
      permittedRoles={[PERMISSIONS.BROKER_MANAGEMENT_READ]}
    />
    <Route
      path={`/:tenantId/broker`}
      layout={MenuLayout}
      component={BrokerPage}
      permittedRoles={[PERMISSIONS.BROKER_MANAGEMENT_READ]}
    />
    <Route
      path={`/:tenantId/name_change_request`}
      exact
      layout={MenuLayout}
      component={NameChangeRequestPage}
      permittedRoles={[PERMISSIONS.NAME_CHANGE_TRANSACTION_MANAGEMENT_READ]}
    />
    <Route
      path="/:tenantId/order/:id"
      exact
      layout={MenuLayout}
      component={OrderDetailPage}
      permittedRoles={[]}
    />
    <Route
      path={`/:tenantId/order`}
      layout={MenuLayout}
      component={OrderPage}
      permittedRoles={[]}
    />

    <Redirect withPermissions to="/:tenantId/enrollment" />
  </Switch>
);

interface RoutesProps {
  initialized: boolean;
  isAuthorized: boolean;

  hasTokens: boolean;
  isLoggingAD: boolean;
  currentUser: any;

  currentWorkspace: any;
  // defaultWorkspace: any;
  // currentWorkspaceError: any;
  // workspaceLoadTimeout: boolean;
}

const Routes = (props: RoutesProps) => {
  if (!props.initialized) {
    console.log("Render null");
    return null;
  } else if (props.isLoggingAD) {
    return <HandleAdRedirectPages />;
  } else if (!props.isAuthorized) {
    console.log("Render GuestPages");
    return <GuestPages />;
  } else {
    const isMaster = props.currentWorkspace?.isMaster || false;

    return (
      <Suspense fallback={<Loader />}>
        {isMaster ? <MasterPages /> : <TenantedPages />}
      </Suspense>
    );
  }
};

export const API = {
  GET_APP_INFO: gql`
    query GET_APP_INFO {
      info {
        hasFirstAdmin
      }
    }
  `,
  GET_CURRENT_USER: gql`
    query GET_CURRENT_USER {
      getCurrentBackofficeUser {
        id
        firstName
        lastName
        ownerType
        email
        activeTimeExpirationInSec
        admin {
          id
          code
          firstName
          lastName
        }
        role {
          title
          permissions
        }
        accessibleTenants
      }
    }
  `,
  GET_WORKSPACE: gql`
    query GET_WORKSPACE($id: String!) {
      workspace(id: $id) {
        id
        name
        description
        isActive
        isMaster
      }
    }
  `,
};

const enhancer = compose(
  withStores((stores: { appStore: { currentUser: any } }) => ({
    currentUser: stores.appStore.currentUser,
  })),
  withTranslation({ prefix: "routes.Routes" }),
  withHooks((props: any, hooks: any) => {
    const { currentUser, t } = props;
    const { useQuery, useMemo, useEffect, useLazyQuery, useState } = hooks;
    const [isSetCurrentUser, setIsSetCurrentUser] = useState(false);

    const [fetchCurrentUser, newCurrentUserResponse] = useLazyQuery(
      API.GET_CURRENT_USER,
      {
        onCompleted: (data: any) => {
          appStore.setCurrentUser(data.getCurrentBackofficeUser);
          setIsSetCurrentUser(true);
        },
      }
    );

    const tenantId = getTenantIdFromUrl();

    const {
      loading: currentWorkspaceLoading,
      data: currentWorkspaceData,
    } = useQuery(API.GET_WORKSPACE, {
      variables: {
        id: tenantId,
      },
      skip: !tenantId || !currentUser,
      fetchPolicy: "network-only",
    });

    const token = getToken();
    const refreshToken = getRefreshToken();

    useEffect(() => {
      if (token && refreshToken) {
        fetchCurrentUser();
      }
    }, [fetchCurrentUser, token, refreshToken]);

    const isLoggingAD = getLoggingAd();

    const initialized =
      ((token && refreshToken && isSetCurrentUser) ||
        (!token && !refreshToken)) &&
      !newCurrentUserResponse.loading &&
      !currentWorkspaceLoading;

    const isAuthorized = currentUser && isSetCurrentUser;
    const hasTokens = token && refreshToken;

    useEffect(() => {
      const $splashScreen = document.getElementById("splash-screen");
      if ($splashScreen) {
        const display = initialized ? "none" : "";
        $splashScreen.style.display = display;
      }
    }, [initialized]);

    useEffect(() => {
      if (
        isAuthorized &&
        !currentWorkspaceLoading &&
        isEmpty(currentWorkspaceData)
      ) {
        const currentTenantId = currentUser?.accessibleTenants?.[0];
        window.location.href = getTenantPath(
          homePath(currentUser.role.permissions),
          currentTenantId
        );
      }
    }, [
      isAuthorized,
      currentUser,
      currentWorkspaceData,
      currentWorkspaceLoading,
    ]);

    const { accounts, instance, inProgress } = useMsal();

    const logout = useCallback(() => {
      instance.setActiveAccount(null);
      appStore.removeAllTokens();
    }, [instance]);

    const [isExpireTimeAlert, setIsExpireTimeAlert] = useState(false);

    const timeoutValue = currentUser?.activeTimeExpirationInSec;
    const timeout = timeoutValue ? timeoutValue * 1000 : 1000 * 900;

    const expireTimeAlert = () => {
      setIsExpireTimeAlert(true);
      logout();
      const timeoutInMinutes = timeout / 1000 / 60;
      // @ts-ignore
      Modal.alert({
        title: t(".expireTimeAlertTitle"),
        children: t(".expireTimeAlertChildren", { timeout: timeoutInMinutes }),
        okButtonLabel: t(".expireTimeAlertOkButtonLabel"),
        okButtonVariant: "contained",
        onOk: async () => {
          // @ts-ignore
          Modal.close();
          setIsExpireTimeAlert(false);
          window.location.reload();
        },
        disableBackdropClick: true,
      });
    };
    console.log(timeoutValue);

    const onIdle = () => {
      if (!timeoutValue) return;
      const token = getToken();
      if (token) expireTimeAlert();
    };

    const onActive = () => {};
    const onAction = () => {
      if (!isExpireTimeAlert) {
        localStorage.setItem("lastActiveTime", new Date().toISOString());
      }
    };

    const { getRemainingTime, getLastActiveTime } = useIdleTimer({
      onIdle,
      onActive,
      onAction,
      crossTab: true,
      timeout,
      events: [
        "mousemove",
        "keydown",
        "mousedown",
        "touchstart",
        "wheel",
        "mousewheel",
      ],
    });

    return {
      initialized,
      isAuthorized,

      hasTokens,
      isLoggingAD,
      currentUser,

      currentWorkspace: currentWorkspaceData?.workspace,
    };
  })
);

export default enhancer(Routes);
